import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize} from '@/components/common/content/ButtonTypes'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {usePreferences} from '@/components/context/Preferences'
import {GuideSelectionContentBox} from '@/components/guide/GuideSelectionContentBox'
import {getClassAssistContent} from '@/content/classAssist'
import {getCountryCards} from '@/content/countryCards'
import {Link, useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {useLanguage} from '@/utils/i18n/language'
import {calculateCountryPrice} from '@/utils/pricing'

import {Text} from '../../common/content/Text'
import {ArrowIcon} from '../../common/icons/ArrowIcon'

type ClassAssistEstimatedPriceProps = {
  sectorName: string
  numOfClasses: number
}

export const ClassAssistEstimatedPrice: FC<ClassAssistEstimatedPriceProps> = ({
  sectorName,
  numOfClasses,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {currency, origin, destination} = usePreferences()
  const {estimatedPriceTitle, estimatedPriceDescription, pricingButtonText, pricingButtonLink} =
    getClassAssistContent(language)
  const regionalCountryCards = getCountryCards(language, origin, destination, 4)

  return (
    <>
      <GuideSelectionContentBox title={t(estimatedPriceTitle, {name: sectorName})}>
        <Text>{t(estimatedPriceDescription)}</Text>
        <ul className="list">
          {regionalCountryCards.map(({key, title, imageSrc, imageAlt}) => (
            <li key={key} className="item">
              <div className="country">
                <div className="image-wrapper">
                  <Image
                    src={imageSrc}
                    alt={imageAlt}
                    style={{objectFit: 'contain'}}
                    width={32}
                    height={32}
                  />
                </div>
                <Text>{title}</Text>
              </div>
              <div className="price">
                <Text color={theme.palette.text.light} align="right">
                  <LocalizedNumberFormat
                    value={calculateCountryPrice(key, currency, numOfClasses, 0, 0).total.vatBase}
                    type="currency"
                  />
                </Text>
              </div>
            </li>
          ))}
        </ul>
        <div className="button-wrapper">
          <Link href={pricingButtonLink}>
            <Button
              type="button"
              icon={<ArrowIcon size={IconSize.small} />}
              size={ButtonSize.small}
              isHoverable
            >
              {t(pricingButtonText)}
            </Button>
          </Link>
        </div>
      </GuideSelectionContentBox>

      <style jsx>{`
        .list {
          margin-top: ${theme.spacing[5]};
        }

        .item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .item + .item {
          margin-top: ${theme.spacing[3]};
        }

        .country {
          display: flex;
          align-items: center;
        }

        .image-wrapper {
          position: relative;
          width: 32px;
          height: 32px;
          margin-right: ${theme.spacing[3]};
          border-radius: 50%;
          box-shadow: 0px 0px 0px 1px ${theme.palette.gray[200]};
        }

        .price {
          margin-left: ${theme.spacing[3]};
          white-space: nowrap;
        }

        .button-wrapper {
          margin-top: ${theme.spacing[5]};
        }
      `}</style>
    </>
  )
}
